import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  IconButton,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useContext, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import C from '@shared/constants/general';
import { ConferenceContext, AuthContext } from '@/store';
import { markdownMini, addExtraInfoToEvent } from '@/lib/utils/DataFormat';
import { timeStringToNum } from '@/lib/utils/Sorters';
import UserAvatar from '@/components/ui/userAvatar';

const hourStyles = (theme, props) => ({
  list: {
    width: `${props.width}px !important`,
    fontSize: '13px !important',
    position: 'absolute !important',
    left: '0 !important',
    top: `${props.roomTitleHeight - 8}px !important`,
  },
  item: {
    height: `${props.height}px !important`,
  },
  full: {
    height: `${props.height / 2}px !important`,
    fontWeight: '600 !important',
  },
  half: {
    height: `${props.height / 2}px !important`,
    opacity: '0.5 !important',
  },
});

const range = (start, end) =>
  Array.from({ length: end - start }, (v, k) => k + start);

const HourGrid = React.memo(
  (props) => {
    const classes = hourStyles(useTheme(), {
      gridcolor: props.gridcolor,
      width: props.width,
      height: props.height,
      roomTitleHeight: props.roomTitleHeight,
    });
    const hourRange = range(props.start, props.end);
    return (
      <Box sx={classes.list}>
        {hourRange.map((h) => (
          <Box key={h} sx={classes.item}>
            <Box sx={classes.full}>{h.toString().padStart(2, '0')}:00</Box>
            <Box sx={classes.half}>{h.toString().padStart(2, '0')}:30</Box>
          </Box>
        ))}
      </Box>
    );
  },
  (prevProps, nextProps) =>
    prevProps.start === nextProps.start &&
    prevProps.end === nextProps.end &&
    prevProps.updateStepper === nextProps.updateStepper
);

const gridBGStyles = (theme, props) => ({
  gridBg: {},
  room: {
    width: `${props.roomWidth} !important`,
    minWidth: '200px !important',
    textAlign: 'center !important',
    height: `${props.roomTitleHeight}px !important`,
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    lineHeight: '0.9 !important',
  },
  eventList: {
    '&::after': {
      content: '""',
      display: 'block !important',
      position: 'absolute !important',
      top: '0 !important',
      right: '0 !important',
      width: '1px !important',
      backgroundColor: `${props.bgColor} !important`,
      height: `${props.listHeight}px !important`,
      opacity: '0.5 !important',
    },
    '&:last-child::after': {
      display: 'none !important',
    },
    position: 'relative !important',
    width: `${props.roomWidth} !important`,
    minWidth: '200px !important',
    height: `${props.listHeight}px !important`,
    display: 'inline-block !important',
    background: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" height="${
      props.height / 2
    }px" width="100px"><rect width="100px" height="1px" fill="${
      props.bgColorEscaped
    }" fill-opacity="0.3" /></svg>') !important`,
  },
  roomLink: {
    color: `${theme.palette.primary.main} !important`,
    textDecoration: 'none !important',
  },
  eventItem: {
    fontSize: '13px !important',
    color: '#ffffff !important',
    backgroundColor: '#333333BB !important',
    borderRadius: '3px !important',
    fontWeight: 'bold !important',
    position: 'absolute !important',
    left: '4px !important',
    right: '5px !important',
    padding: '5px !important',
    minHeight: '20px !important',
    overflow: 'hidden !important',
    textDecoration: 'none !important',
    '&:hover': {
      textDecoration: 'none !important',
      zIndex: '800 !important',
      backgroundColor: '#333333 !important',
    },
  },
  eventPause: {
    opacity: '0.5 !important',
  },
  eventTimeInfo: {
    fontWeight: 'normal !important',
    fontSize: '12px !important',
    opacity: '0.7 !important',
    whiteSpace: 'pre-wrap !important',
  },
  eventTitleLive: {
    paddingRight: '5px !important',
    lineHeight: '14px !important',
    fontSize: '14px !important',
    color: '#f44336 !important',
  },
  eventVideo: {
    color: `${theme.palette.primary.main} !important`,
  },
  eventVideoIcon: {
    width: '12px !important',
    height: '14px !important',
    fontSize: '15px !important',
    paddingTop: '2px !important',
  },
});

const EventGrid = React.memo(
  (props) => {
    const {
      conferenceState: { conferenceDetail },
    } = useContext(ConferenceContext);
    const allRooms = props.totalFavorites.length
      ? [
          {
            id: 'xxx',
            title: 'Favorites',
            events: props.totalFavorites,
            video_state: C.PROGRAM_ROOM.VIDEO_STATE.NONE,
            programroomvideoassets: [],
          },
          ...props.programDay.programRooms,
        ]
      : props.programDay.programRooms;
    const classes = gridBGStyles(useTheme(), {
      roomWidth: `${100 / allRooms.length}%`,
      height: props.height,
      listHeight: (props.end - props.start) * props.height,
      bgColorEscaped: escape(props.gridcolor),
      bgColor: props.gridcolor,
      roomTitleHeight: props.roomTitleHeight,
    });

    const handleEventClick = (e, ev) => {
      e.preventDefault();
      props.setViewEvent(ev);
    };

    const calcTop = (timeStart, timeEnd, overlapsPreviousEvent) => {
      const calcTime = timeStringToNum(timeStart) / 3600 - props.start;
      const calcHeight =
        timeEnd === null
          ? 1
          : timeStringToNum(timeEnd) / 3600 - calcTime - props.start;
      return {
        top: calcTime * props.height,
        height: calcHeight * props.height - 2,
        left: overlapsPreviousEvent ? 15 : 0,
      };
    };

    return (
      <Box sx={classes.gridBg}>
        <Box display="flex">
          {allRooms.map((room) => (
            <Box key={room.id} sx={classes.room}>
              {room.id === 'xxx' && (
                <span>
                  <Icon>star</Icon>
                </span>
              )}
              {window.SETTINGS.HIDE_PROGRAM_ROOMS_IN_MENU ||
              room.id === 'xxx' ? (
                <strong>{room.title}</strong>
              ) : (
                <Link sx={classes.roomLink} href={`/live/room/${room.id}`}>
                  <strong>{room.title}</strong>
                </Link>
              )}
              {/* {room.id === 'xxx' && (
                <Button
                  size="small"
                  color="primary"
                  endIcon={<Icon>event</Icon>}
                  target="_blank"
                  href={`/api/visitor/conferences/${conferenceDetail.id}/downloadfavorites`}
                >
                  Download
                </Button>
              )} */}
            </Box>
          ))}
        </Box>
        <Box display="flex">
          {allRooms.map((room) => (
            <Box key={room.id} sx={classes.eventList}>
              {room.events.map((e, index) => {
                const roomData = e && (e.roomData || room);

                const canViewEvent =
                  e.type !== C.EVENT.TYPE.ACTIVITY &&
                  roomData.video_state === C.PROGRAM_ROOM.VIDEO_STATE.FILE &&
                  roomData.programroomvideoassets &&
                  roomData.programroomvideoassets.length > 0;

                const endTime =
                  e.end_time ||
                  (index + 1 < room.events.length &&
                    room.events[index + 1].start_time) ||
                  null;
                // Find if there's an overlap from previous event
                const prevEvent = index > 0 ? room.events[index - 1] : null;
                const prevEndTime = prevEvent?.end_time || null;
                const prevOverlap =
                  prevEndTime &&
                  timeStringToNum(prevEndTime) > timeStringToNum(e.start_time);

                // const prevEvent = index > 0 ? room.events[index - 1] : null;
                // const prevEndTime =
                //   prevEvent && prevEvent.end_time ? prevEvent.end_time : null;
                // const prevOverlap =
                //   prevEndTime &&
                //   timeStringToNum(prevEndTime) > timeStringToNum(e.start_time);
                // const overlap = prevOverlap ? 'overlap' : '';

                const styleCalc = calcTop(e.start_time, endTime, prevOverlap);
                const timeStr = `${e.start_time.substring(0, 5)}-${
                  endTime ? endTime.substring(0, 5) : ''
                }`;

                return e.type !== C.EVENT.TYPE.ACTIVITY ? (
                  <Link
                    href="#"
                    onClick={(event) =>
                      handleEventClick(event, {
                        ...e,
                        programRoom: {
                          id: roomData.id,
                          title: roomData.title,
                          programroomvideoassets:
                            roomData.programroomvideoassets,
                        },
                        timeStr,
                      })
                    }
                    key={e.id}
                    sx={{ ...classes.eventItem }}
                    style={styleCalc}
                  >
                    {roomData.currentEventId === e.id && (
                      <Box
                        sx={classes.eventTitleLive}
                        component="span"
                        color="error"
                        variant="subtitle1"
                      >
                        LIVE
                      </Box>
                    )}
                    {e.title.length > 100
                      ? `${e.title.substring(0, 100)}..`
                      : e.title}
                    <br />
                    <Box component="span" sx={classes.eventTimeInfo}>
                      {timeStr}
                    </Box>
                    {canViewEvent && (
                      <Box component="span" sx={classes.eventVideo}>
                        <Icon sx={classes.eventVideoIcon}>play_arrow</Icon>{' '}
                        video
                      </Box>
                    )}
                    <Box
                      sx={classes.eventTimeInfo}
                      dangerouslySetInnerHTML={markdownMini(
                        e.description.length > 200
                          ? `${e.description.substring(0, 200)}..`
                          : e.description
                      )}
                    />
                  </Link>
                ) : (
                  <Box
                    key={e.id}
                    sx={{
                      ...classes.eventItem,
                      ...classes.eventTimeInfo,
                    }}
                    style={styleCalc}
                  >
                    {e.title.length > 100
                      ? `${e.title.substring(0, 100)}..`
                      : e.title}
                    <div
                      dangerouslySetInnerHTML={markdownMini(
                        e.description.length > 200
                          ? `${e.description.substring(0, 200)}..`
                          : e.description
                      )}
                    />
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
        {/* {props.children} */}
      </Box>
    );
  },
  (prevProps, nextProps) =>
    prevProps.programDayId === nextProps.programDayId &&
    prevProps.totalFavorites.length === nextProps.totalFavorites.length &&
    prevProps.updateStepper === nextProps.updateStepper
);
EventGrid.displayName = 'EventGrid';

const MIN_EVENT_LENGTH = 3600; // 1 hour
const getFirstAndLastEventTime = (programDay) => {
  let firstStartTime = null;
  let lastEndTime = null;
  programDay.programRooms.forEach((room) => {
    room.events.forEach((ev) => {
      const calcStartTime = timeStringToNum(ev.start_time);
      const calcEndTime = ev.end_time
        ? timeStringToNum(ev.end_time)
        : calcStartTime + MIN_EVENT_LENGTH;
      if (firstStartTime === null || calcStartTime < firstStartTime) {
        firstStartTime = calcStartTime;
      }
      if (lastEndTime === null || calcEndTime > lastEndTime) {
        lastEndTime = calcEndTime;
      }
    });
  });
  return {
    firstHour: firstStartTime === null ? 8 : Math.floor(firstStartTime / 3600),
    lastHour: lastEndTime === null ? 16 : Math.ceil(lastEndTime / 3600),
  };
};

const useStyles = (theme, props) => ({
  holder: {
    position: 'relative !important',
  },
  calendar: {
    paddingLeft: `${props.hourWidth}px !important`,
    position: 'relative !important',
    overflowX: 'auto !important',
  },
  content: {
    width: '1400px !important',
  },
  dialogBg: {
    minWidth: '300px !important',
    [`${theme.breakpoints.up('md')}`]: {
      minWidth: '600px !important',
    },
    backgroundColor: `${theme.palette.background.shadedown_20} !important`,
  },
  roomLink: {
    color: 'inherit !important',
  },
  eventDescription: {
    whiteSpace: 'pre-wrap !important',
  },
  videoFile: {
    // Keep
    position: 'absolute !important',
    top: '0 !important',
    left: '0 !important',
    right: '0 !important',
    bottom: '0 !important',
    '&::after': {
      zIndex: '1 !important',
      position: 'absolute !important',
      top: '0 !important',
      left: '0 !important',
      right: '0 !important',
      bottom: '0 !important',
      content: '""',
      display: 'block !important',
      backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
    },
    '&:hover': {
      '&::after': {
        display: 'none !important',
      },
    },
    '& > *': {
      zIndex: '2 !important',
      display: 'relative !important',
    },
  },
  videoFileHolder: {
    // Keep
    cursor: 'pointer !important',
    marginTop: '5px !important',
    color: '#fff !important',
    backgroundColor: '#000 !important',
    width: '100% !important',
    height: '0px !important',
    position: 'relative !important',
    backgroundPosition: 'size !important',
    backgroundSize: 'cover !important',
    borderRadius: '2px !important',
    paddingBottom: '56.25% !important',
  },
  forumLink: {
    paddingTop: '8px !important',
    color: 'rgba(0, 0, 0, 0.87) !important',
    textDecoration: 'underline !important',
    opacity: '0.4 !important',
    '&:hover': {
      opacity: '1 !important',
    },
  },
  favoriteButton: {},
});

export const ProgramViewGrid = React.memo(
  (props) => {
    const navigate = useNavigate();
    const HOUR_WIDTH = 50;
    const HOUR_HEIGHT = 160;
    const ROOM_HEIGHT = 400;
    const ROOM_TITLE_HEIGHT = 40;
    const GRID_COLOR = '#999999';
    const theme = useTheme();
    const classes = useStyles(theme, {
      hourWidth: HOUR_WIDTH,
      roomHeight: ROOM_HEIGHT,
      roomTitleHeight: ROOM_TITLE_HEIGHT,
    });
    const { authState } = useContext(AuthContext);
    const [viewEvent, setViewEvent] = useState(null);
    const timeRange = useMemo(
      () => getFirstAndLastEventTime(props.programDay),
      [props.programDay]
    );

    const { openUserPopup, toggleFavorite, conferenceState } = useContext(
      ConferenceContext
    ); // Hämta Context

    const roomData = viewEvent && (viewEvent.roomData || viewEvent.programRoom);

    let canViewEvent = false;
    canViewEvent =
      viewEvent !== null &&
      viewEvent.type !== C.EVENT.TYPE.ACTIVITY &&
      roomData.programroomvideoassets !== null &&
      roomData.programroomvideoassets.length > 0;

    const handleModalClose = () => {
      setViewEvent(null);
    };
    const gotoEvent = (eventId) => {
      //history.push(`/live/session/${eventId}`);
      // location.replace(`/live/session/${eventId}`); //TODO: Ugly need to fix states in EventDashBoard.
      navigate(`/live/session/${eventId}`, { replace: true }); //Using replace: true to match the previous behavior
    };
    if (viewEvent) {
      //Add a props to event in order to calculate offest and startasset etc
      let extra_props = {
        programDay: {
          date: props.programDay.date,
          conference: { timezone: conferenceState.conferenceDetail.timezone },
        },
      };

      viewEvent['programDay'] = extra_props.programDay;
      //change by reference
      addExtraInfoToEvent(viewEvent);
    }
    const triggerToggleFavorite = () => {
      toggleFavorite(viewEvent.id, viewEvent.favorites.length === 0).then(
        () => {
          setViewEvent((curr) => ({
            ...curr,
            favorites: viewEvent.favorites.length
              ? []
              : [{ id: authState.user.id }],
          }));
        }
      );
    };
    console.log('viewEvent', viewEvent);
    return (
      <Box sx={classes.holder}>
        <Dialog
          open={viewEvent !== null}
          onClose={handleModalClose}
          scroll="paper"
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
          slotProps={{ paper: { sx: classes.dialogBg } }}
        >
          {viewEvent && (
            <>
              <DialogTitle id="scroll-dialog-title">
                <Box display="flex" alignItems="flex-start">
                  <Box flexGrow={1}>{viewEvent.title}</Box>
                  {viewEvent.favorites && (
                    <Box>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={triggerToggleFavorite}
                        sx={classes.favoriteButton}
                      >
                        <Icon>
                          {viewEvent.favorites.length > 0
                            ? 'star'
                            : 'star_border'}
                        </Icon>
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </DialogTitle>
              <DialogContent dividers={true}>
                {canViewEvent && viewEvent.start_asset && (
                  <Box
                    sx={classes.videoFileHolder}
                    onClick={() => gotoEvent(viewEvent.id)}
                    style={{
                      backgroundImage: `url(/api/redirect_mux_image/${viewEvent?.start_asset?.mux_asset_playback_id}/thumb/${viewEvent?.start_asset?.start_offest_seconds})`,
                    }}

                    /*style={{
                      backgroundImage: `url(https://image.mux.com/${viewEvent.EventAssets[0].mux_asset_playback_id}/thumbnail.jpg?token=${viewEvent.EventAssets[0].thumb_jwt})`,
                    }}
                    */
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={classes.videoFile}
                    >
                      <Icon>play_arrow</Icon>
                      <Box>
                        <strong>Replay</strong>
                      </Box>
                    </Box>
                  </Box>
                )}

                <DialogContentText id="scroll-dialog-description" tabIndex={-1}>
                  <span>
                    {viewEvent.timeStr}{' '}
                    {window.SETTINGS.HIDE_PROGRAM_ROOMS_IN_MENU ? (
                      <strong>{roomData.title}</strong>
                    ) : (
                      <RouterLink
                        sx={classes.roomLink}
                        to={`/live/room/${roomData.id}`}
                      >
                        <strong>{roomData.title}</strong>
                      </RouterLink>
                    )}
                  </span>
                  <br />
                  <Box
                    sx={classes.eventDescription}
                    dangerouslySetInnerHTML={markdownMini(
                      viewEvent.description
                    )}
                  />

                  {/* {!window.SETTINGS.NEW_DISCUSSION_MODERATE && !canViewEvent && (
                    <>
                      <br />
                      <Link
                        href={`/live/session/${viewEvent.id}`}
                        sx={classes.forumLink}
                      >
                        Forum
                      </Link>
                    </>
                  )} */}
                </DialogContentText>
                {viewEvent.visitors.length > 0 && (
                  <Box display="flex" py={1}>
                    {viewEvent.visitors.map((v) => (
                      <Box key={v.id} pr={1}>
                        <ButtonBase
                          onClick={() => openUserPopup(v)}
                          disabled={!authState.userLoggedIn}
                        >
                          <UserAvatar size="medium" user={v} />
                        </ButtonBase>
                      </Box>
                    ))}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleModalClose} color="primary">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
        <Box sx={classes.calendar}>
          <EventGrid
            start={timeRange.firstHour}
            end={timeRange.lastHour}
            setViewEvent={setViewEvent}
            gridcolor={GRID_COLOR}
            roomTitleHeight={ROOM_TITLE_HEIGHT}
            height={HOUR_HEIGHT}
            programDay={props.programDay}
            programDayId={props.programDayId}
            totalFavorites={props.totalFavorites}
            updateStepper={props.updateStepper}
          />
        </Box>
        <HourGrid
          start={timeRange.firstHour}
          end={timeRange.lastHour}
          roomTitleHeight={ROOM_TITLE_HEIGHT}
          gridcolor={GRID_COLOR}
          height={HOUR_HEIGHT}
          width={HOUR_WIDTH}
          updateStepper={props.updateStepper}
        />
      </Box>
    );
  },
  (prevProps, nextProps) =>
    prevProps.programDayId === nextProps.programDayId &&
    prevProps.totalFavorites.length === nextProps.totalFavorites.length &&
    prevProps.updateStepper === nextProps.updateStepper
);
